"use client";

import { useCallback } from "react";
import { confirmPasswordResetAction } from "@/actions/serverActions/auth/confirmPasswordResetAction";
import { loginAction } from "@/actions/serverActions/auth/loginAction";
import { logoutAction } from "@/actions/serverActions/auth/logoutAction";
import { refreshSessionAction } from "@/actions/serverActions/auth/refreshSessionAction";
import { requestPasswordResetAction } from "@/actions/serverActions/auth/requestPasswordResetAction";
import { getFullPath } from "@/config/basePath";
import { clearCsrfTokenCache } from "@/lib/csrf-client";
import { useAlert } from "@/Provider/AlertProvider/AlertProvider";
import {
  clearAuthForm,
  setIsLoading,
} from "@/store/Features/states/auth-management/authSlice";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { useServerAction } from "./useServerAction";

/**
 * Auth hook — wires React UI to all auth server actions.
 *
 * Surface:
 *   { email, password, dispatch,
 *     handleLogin, handleLogout, handleRefreshSession,
 *     requestPasswordReset, confirmPasswordReset }
 *
 * **Password reset diverges from the other CashlessAI apps** — auth-gateway's
 * flow is request → emailed link → confirm, with no OTP step. Do not port a
 * sibling app's three-step reset back in.
 *
 * `email`, `password`, and `isLoading` live in Redux (`state.auth`) so the
 * form fields are controlled and survive re-renders. Tokens never reach the
 * client — only the encrypted Redis session does.
 */
export function useAuth() {
  const dispatch = useAppDispatch();
  const { email, password } = useAppSelector((state) => state.auth);
  const { showError, showSuccess } = useAlert();

  // ── Bind every action ONCE; CSRF auto-injected by useServerAction ──────
  const login = useServerAction(loginAction);
  const logout = useServerAction(logoutAction);
  const refreshSession = useServerAction(refreshSessionAction);
  const requestPasswordReset = useServerAction(requestPasswordResetAction);
  const confirmPasswordReset = useServerAction(confirmPasswordResetAction);

  // ── Login ──────────────────────────────────────────────────────────────
  const handleLogin = useCallback(
    async (_remember?: boolean) => {
      dispatch(setIsLoading(true));
      try {
        const result = await login({ email, password });
        const needs2FA =
          "requiresTwoFactor" in result && result.requiresTwoFactor;
        if (result.success && !needs2FA) {
          showSuccess(result.message ?? "Login successful");
          window.location.href = getFullPath("/");
        } else if (!result.success) {
          // 403 = account deactivated (`is_active=False`, auth.md §1.1). The
          // backend message is generic, so show accurate guidance instead of
          // the raw "Invalid credentials"-style text.
          const status = "status" in result ? result.status : undefined;
          showError(
            status === 403
              ? "Your account has been deactivated. Please contact your manager."
              : (result.message ?? "Login failed"),
          );
        }
        // requiresTwoFactor === true → LoginForm handles the challenge UI
        return result;
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [login, email, password, dispatch, showError, showSuccess],
  );

  // ── Logout ─────────────────────────────────────────────────────────────
  const handleLogout = useCallback(async () => {
    dispatch(setIsLoading(true));
    try {
      await logout();
      clearCsrfTokenCache();
      dispatch(clearAuthForm());
      window.location.href = getFullPath("/auth/login");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Logout failed";
      showError(message);
    } finally {
      dispatch(setIsLoading(false));
    }
  }, [logout, dispatch, showError]);

  // ── Refresh session (idle-timeout extender) ────────────────────────────
  const handleRefreshSession = useCallback(async (): Promise<boolean> => {
    try {
      const result = await refreshSession();
      return result.success;
    } catch {
      return false;
    }
  }, [refreshSession]);

  /**
   * ── Password reset — TWO steps, no OTP ─────────────────────────────────
   *
   * Step 1 emails a link carrying a `token`; step 2 posts that token with the
   * new password. The middle "verify OTP" step this hook used to expose had no
   * endpoint behind it and has been removed — see
   * `docs/pos-api/api-req/answers/DONE-2026-08-05-password-reset-paths-answers.md`.
   */

  /** Step 1 — request the email. Its message is deliberately non-committal. */
  const requestPasswordResetHandler = useCallback(
    async (emailArg: string) => {
      const result = await requestPasswordReset({ email: emailArg });
      // Never surfaced as an error on a 200: the endpoint cannot distinguish an
      // unknown address from a known one, and the UI must not appear to either
      return result;
    },
    [requestPasswordReset],
  );

  /**
   * Step 2 — set the new password with the token from the emailed link.
   *
   * On success the caller must route to **login**: no session is returned and
   * every existing one is revoked.
   */
  const confirmPasswordResetHandler = useCallback(
    async (token: string, newPassword: string) => {
      const result = await confirmPasswordReset({ token, newPassword });
      if (result.success) {
        showSuccess(result.message ?? "Password updated. Please sign in.");
      } else {
        showError(result.message ?? "Password reset failed");
      }
      return result;
    },
    [confirmPasswordReset, showError, showSuccess],
  );

  return {
    // Form state from Redux
    email,
    password,
    dispatch,

    // Login / logout
    handleLogin,
    handleLogout,
    handleRefreshSession,

    // Password reset — two steps. `verifyResetOtp` and `resetPasswordWithOtp`
    // are gone: there is no OTP in this flow and never was.
    requestPasswordReset: requestPasswordResetHandler,
    confirmPasswordReset: confirmPasswordResetHandler,
  };
}
